import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AssistantMessageContent from "../../../components/AssistantMessageContent";
import { useAuthContext } from "../../../context/AuthContext";
import {
  createInternalAskSession,
  chatInternalAsk,
  getInternalAskStatus,
} from "../../../api/client";

function errMsg(err, fallback) {
  const facing = err?.userFacing;
  const fromFacing = typeof facing === "string" ? facing : facing?.message;
  return (
    fromFacing ||
    err?.response?.data?.message ||
    (typeof err?.message === "string" ? err.message : "") ||
    fallback
  );
}

export default function InternalAskSectionPage() {
  const { t } = useTranslation("app");
  const { userData } = useAuthContext();
  const ctx = useOutletContext() || {};
  const stopRefreshing = typeof ctx.stopRefreshing === "function" ? ctx.stopRefreshing : () => {};
  const registerRefresh = typeof ctx.registerRefresh === "function" ? ctx.registerRefresh : () => {};
  const [sessionId, setSessionId] = useState("");
  const [indexMeta, setIndexMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loadingSession, setLoadingSession] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);

  const isPlatformScope = userData?.scope === "platform";

  const suggestions = useMemo(
    () => [
      t("platformPage.internalAskSuggestion1"),
      t("platformPage.internalAskSuggestion2"),
      t("platformPage.internalAskSuggestion3"),
    ],
    [t]
  );

  const loadSession = useCallback(async () => {
    setLoadingSession(true);
    setError("");
    setSessionId("");
    setIndexMeta(null);
    if (!isPlatformScope) {
      setMessages([]);
      setLoadingSession(false);
      stopRefreshing();
      return;
    }
    try {
      const [sess, status] = await Promise.all([
        createInternalAskSession(),
        getInternalAskStatus().catch(() => null),
      ]);
      setSessionId(String(sess?.sessionId || ""));
      setIndexMeta(status || sess?.index || null);
      setMessages([]);
    } catch (err) {
      setError(errMsg(err, t("platformPage.internalAskSessionError")));
      setMessages([]);
    } finally {
      setLoadingSession(false);
      stopRefreshing();
    }
  }, [isPlatformScope, stopRefreshing, t]);

  useEffect(() => {
    registerRefresh(() => {
      loadSession();
    });
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing, loadSession]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!listRef.current) return;
    try {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    } catch {
      /* ignore */
    }
  }, [messages, sending]);

  const indexReady = Boolean(indexMeta?.ready);
  const hasConversation = messages.some((m) => m.role === "user");
  const showIndexEmpty = isPlatformScope && !indexReady && !loadingSession && !error;

  const handleSend = async (preset) => {
    const msg = String(preset || input || "").trim();
    if (!msg || sending || !sessionId || !indexReady || !isPlatformScope) return;
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setSending(true);
    setError("");
    try {
      const resp = await chatInternalAsk({
        sessionId,
        userMessage: msg,
        history: next.slice(-12),
      });
      const reply = String(resp?.reply || "").trim() || t("platformPage.internalAskEmptyReply");
      setMessages((cur) => [...cur, { role: "assistant", content: reply }]);
      if (resp?.index) setIndexMeta((prev) => ({ ...(prev || {}), ...resp.index }));
    } catch (err) {
      setMessages((cur) => [
        ...cur,
        { role: "assistant", content: errMsg(err, t("platformPage.internalAskChatError")) },
      ]);
    } finally {
      setSending(false);
    }
  };

  const composerDisabled = sending || !sessionId || !indexReady || !isPlatformScope;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        bgcolor: "background.paper",
        borderColor: "divider",
        overflow: "hidden",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "70vh", md: "calc(100vh - 180px)" },
      })}
    >
      <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ mb: 2 }}>
        <Box
          sx={(theme) => ({
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
            flexShrink: 0,
          })}
        >
          <PsychologyOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 650, letterSpacing: -0.02, lineHeight: 1.25 }}>
            {t("platformPage.tabInternalAsk")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, maxWidth: 640 }}>
            {t("platformPage.internalAskIntro")}
          </Typography>
        </Box>
        {hasConversation ? (
          <Button
            size="small"
            variant="outlined"
            onClick={loadSession}
            disabled={loadingSession || sending}
          >
            {t("platformPage.internalAskNewConversation")}
          </Button>
        ) : null}
      </Stack>

      {!isPlatformScope && !loadingSession ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/platform/login">
              {t("platformPage.internalAskPlatformLoginCta")}
            </Button>
          }
        >
          {t("platformPage.internalAskPlatformScopeRequired")}
        </Alert>
      ) : null}

      {showIndexEmpty ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
          {t("platformPage.internalAskIndexEmpty")}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {loadingSession ? (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box
            ref={listRef}
            sx={(theme) => ({
              flex: "1 1 auto",
              overflow: "auto",
              borderRadius: 2,
              p: { xs: 1.5, md: 2 },
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              minHeight: 280,
            })}
          >
            {!hasConversation ? (
              <Box sx={{ py: { xs: 3, md: 6 }, px: 1, maxWidth: 560, mx: "auto", textAlign: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.75 }}>
                  {t("platformPage.internalAskWelcomeTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                  {t("platformPage.internalAskWelcomeBody")}
                </Typography>
                <Stack direction="column" gap={1} alignItems="stretch">
                  {suggestions.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      onClick={() => handleSend(s)}
                      disabled={composerDisabled}
                      variant="outlined"
                      sx={{
                        height: "auto",
                        py: 1.1,
                        px: 1,
                        justifyContent: "flex-start",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          textAlign: "left",
                          display: "block",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              messages.map((m, idx) => {
                const isUser = m.role === "user";
                return (
                  <Box
                    key={`${m.role}-${idx}`}
                    sx={{
                      display: "flex",
                      justifyContent: isUser ? "flex-end" : "flex-start",
                      mb: 1.25,
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        maxWidth: { xs: "92%", md: "78%" },
                        px: 1.5,
                        py: 1.1,
                        borderRadius: 2,
                        bgcolor: isUser
                          ? alpha(theme.palette.primary.main, 0.12)
                          : theme.palette.background.paper,
                        border: "1px solid",
                        borderColor: isUser ? "transparent" : "divider",
                      })}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.35 }}>
                        {isUser ? t("platformPage.internalAskYou") : t("platformPage.tabInternalAsk")}
                      </Typography>
                      {isUser ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {m.content}
                        </Typography>
                      ) : (
                        <AssistantMessageContent content={m.content} />
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
            {sending ? (
              <Stack direction="row" alignItems="center" gap={1} color="text.secondary" sx={{ mt: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">{t("platformPage.internalAskThinking")}</Typography>
              </Stack>
            ) : null}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, mb: 1 }}>
            {t("platformPage.internalAskHint")}
          </Typography>

          <Stack direction="row" gap={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("platformPage.internalAskPlaceholder")}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={composerDisabled}
            />
            <IconButton
              color="primary"
              onClick={() => handleSend()}
              disabled={composerDisabled || !input.trim()}
              sx={(theme) => ({
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.18) },
              })}
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </>
      )}
    </Paper>
  );
}
